//#region Components
import React from 'react'
import { Field } from 'react-final-form'
import { makeStyles } from '@material-ui/core/styles'

import { translate } from 'components/shared/internationalization'

import { CircularProgress, Divider, NewDialogTextField } from 'components/shared/StyledComponents'
import { MenuItem } from '@material-ui/core'
import i18n from 'i18next'
import { InputLabelStyled } from 'components/minutetaker/components/form/AssigneeChipInputField'

//#endregion

//#region Styles

const useStyles = makeStyles(() => ({
    sectionFieldContainer: {
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-start',
        alignItems: 'flex-start'
    },
    loadingStateRow: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '4px',
        minHeight: '40px'
    },
    loadingStateLabel: {
        marginTop: 0,
        marginBottom: 0
    },
    selectControlWrapper: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            marginTop: '4px',
            paddingTop: '4px',
            height: '40px'
        }
    },
    editableSectionLabel: {
        marginTop: '-0.1rem'
    },
    sectionSelectField: {
        '& .MuiSelect-selectMenu': {
            paddingTop: '13px'
        }
    },
    immutableFieldRoot: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            marginTop: '4px',
            height: '40px'
        }
    },
    immutableSectionLabel: {
        display: 'inline-block'
    },
    immutableTextField: {
        '& .MuiSelect-selectMenu': {
            paddingTop: '13px'
        },
        '& .MuiInputBase-input.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.38)'
        }
    }
}))

//#endregion

//#region Implementation

/** When set by parent, loading vs empty list is explicit (avoids infinite spinner when fetch completes with zero sections). */
export type SectionsRequestStatus = 'idle' | 'loading' | 'success' | 'error'

export const SelectSectionField = ({
    name,
    sections,
    selectedSection,
    isReadOnly,
    error,
    className,
    sectionsRequestStatus,
    showCreateNewSection = true
}: {
    name: string
    sections: any
    selectedSection?: any
    isReadOnly: boolean
    error: any
    className?: any
    sectionsRequestStatus?: SectionsRequestStatus
    showCreateNewSection?: boolean
}) => {
    const classes = useStyles()

    if (!sections) {
        //Sections may not yet be available right away.
        //Next time we reducer gets them prepared we renew them.
        sections = []
    }
    // Ensure sections is an array (state may have array; normalize if passed as object keyed by id)
    const sectionsArray = Array.isArray(sections)
        ? sections
        : typeof sections === 'object' && sections !== null
        ? Object.values(sections)
        : []

    let items = fillSectionsItemsList(sectionsArray)

    if (showCreateNewSection) {
        addCreateNewItemOption(items)
    }

    items.unshift(
        <MenuItem key="__section_placeholder__" value="" disabled>
            {translate('SELECT_SECTION_PLACEHOLDER')}
        </MenuItem>
    )

    const getSafeSectionValue = (value: any) => {
        if (value == null || value === '' || Number.isNaN(value)) return ''
        const stringValue = String(value)
        const inOptions = items.some(
            (item) =>
                item?.props != null &&
                Object.prototype.hasOwnProperty.call(item.props, 'value') &&
                item.props.value !== undefined &&
                String(item.props.value) === stringValue
        )
        return inOptions ? stringValue : ''
    }

    const hasSectionValue = (value: any) =>
        value != null && value !== '' && !Number.isNaN(value) && String(value) !== '-1'

    // Prefer explicit status from parent; else infer from empty list (cannot distinguish loading vs zero sections).
    const sectionsLoading =
        sectionsRequestStatus !== undefined
            ? sectionsRequestStatus === 'loading'
            : sectionsArray.length === 0

    const rootClassName = [classes.sectionFieldContainer, className].filter(Boolean).join(' ')

    return (
        <Field
            name={name}
            render={({ input: { onChange, value /*, ...rest*/ }, meta: { touched } }) => {
                const safeValue = getSafeSectionValue(value)
                const showSectionLoading = !isReadOnly && sectionsLoading && hasSectionValue(value)

                return (
                    <div className={rootClassName}>
                        {isReadOnly ? (
                            renderImmutableTextField(selectedSection, sectionsArray, classes)
                        ) : showSectionLoading ? (
                            <div className={classes.loadingStateRow}>
                                <InputLabelStyled required className={classes.loadingStateLabel}>
                                    {translate('SECTION_LABEL')}
                                </InputLabelStyled>
                                <CircularProgress size={24} thickness={4} />
                            </div>
                        ) : (
                            <div className={classes.selectControlWrapper}>
                                <InputLabelStyled required className={classes.editableSectionLabel}>
                                    {translate('SECTION_LABEL')}
                                </InputLabelStyled>
                                <NewDialogTextField
                                    select
                                    variant="outlined"
                                    id={i18n.t('SECTION_LABEL')}
                                    className={classes.sectionSelectField}
                                    value={safeValue}
                                    onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                                        onChange(e.target.value)
                                    }
                                    SelectProps={{ displayEmpty: true }}
                                    error={touched && error}
                                    helperText={touched && error}>
                                    {items}
                                </NewDialogTextField>
                            </div>
                        )}
                    </div>
                )
            }}
            type="select"
        />
    )
}

/** Normalize section to flat shape { id, name, sectionType } for both reducer and API shapes */
function normalizeSection(
    val: any
): { id: string | number; name: string; sectionType?: string } | null {
    if (!val || val.id == null || val.id === '') return null
    const attrs = val.attributes ?? val
    const id = val.id ?? attrs.id
    const name = val.name ?? attrs?.name ?? ''
    const sectionType = val.sectionType ?? attrs?.sectionType
    return { id, name, sectionType }
}

const fillSectionsItemsList = (sections: Array<any> = []) => {
    //At some point newly created section coming here when users choose
    //to create new section but dialog not yet closed starting saving action
    //We dont want to show that section in here (we only want to show it when it is actually saved)
    //Show all sections that have id and are not the attendees section (minutes sections + any without sectionType)
    const normalized = sections
        .map(normalizeSection)
        .filter((val): val is NonNullable<ReturnType<typeof normalizeSection>> => val != null)
        .filter((val) => val.sectionType !== 'attendees')

    const items = normalized.map((val) => {
        const name = val.name && val.name.length > 45 ? `${val.name.substr(0, 45)}...` : val.name
        return (
            <MenuItem key={val.id} value={String(val.id)}>
                {name}
            </MenuItem>
        )
    })

    return items
}

const addCreateNewItemOption = (items: Array<any>) => {
    //Add item for creating new section
    if (items.length > 0) {
        items.push(<Divider key={0} />)
    }

    items.push(
        <MenuItem key={1} value={'-1'}>
            {translate('CREATE_NEW_SECTION_DROPDOWN')}
        </MenuItem>
    )
}

type SelectSectionFieldClasses = ReturnType<typeof useStyles>

/** IDs that must not be resolved via String() coercion from bad form/API state */
const isBogusSectionIdString = (id: unknown): boolean => {
    if (id == null || id === '') return true
    if (typeof id === 'number' && Number.isNaN(id)) return true
    const s = String(id)
    return s === 'null' || s === 'undefined' || s === 'NaN'
}

const renderImmutableTextField = (
    valueId: string,
    items: Array<any>,
    classes: SelectSectionFieldClasses
): JSX.Element => {
    const section =
        !isBogusSectionIdString(valueId) && items.find((c) => String(c.id) === String(valueId))
    const text = section ? section.name : ''

    return (
        <div className={classes.immutableFieldRoot}>
            <InputLabelStyled className={classes.immutableSectionLabel}>
                {translate('SECTION_LABEL')}
            </InputLabelStyled>
            <NewDialogTextField
                disabled={true}
                value={text}
                variant="outlined"
                id={valueId ? valueId : ''}
                className={classes.immutableTextField}
            />
        </div>
    )
}

//#endregion

export default SelectSectionField
